import { useQuery } from "@tanstack/react-query";

// Material UI
import { Box, Typography } from "@mui/material";

// Components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { PendingTransferRequestTable } from "../Tables/PendingTransferRequestTable";

// Mock Services
import { getDealerInventoryPendingRequest } from "@/DataPages/api/inventory/queries";
import { getLogger } from "@/Common/helpers/getLogger";

// Hooks
import { useFilteredData } from "../hooks/useFilteredData";

// Constants
import { InventoryTab } from "../constants";
import { MutableRefObject } from "react";

// Types
import { TActionToDo } from "../types/dealerTrades";

const log = getLogger("PendingRequest");

type TransferRequestProps = {
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	hasEditPermission: boolean;
	actionRef: MutableRefObject<TActionToDo>;
	isCurrentPayPeriod: boolean;
};

export default function PendingRequest(props: Readonly<TransferRequestProps>) {
	log("Rendering PendingRequest %O", props);
	const { locationId, hasEditPermission, payPeriodId, commissionCycleId, actionRef, isCurrentPayPeriod } = props;

	const {
		data: pendingRequest,
		isLoading: isLoadingPendingRequest,
		isFetching,
	} = useQuery({
		queryKey: ["inventory", "pendingRequest", locationId, commissionCycleId, payPeriodId],
		queryFn: () => getDealerInventoryPendingRequest(locationId, commissionCycleId, payPeriodId),
		enabled: !!locationId && !!commissionCycleId && !!payPeriodId,
		staleTime: Infinity,
		gcTime: Infinity,
	});

	log("PendingRequest %O", pendingRequest);
	log("IsLoading %O", isLoadingPendingRequest || isFetching);

	const dataPendingRequests = useFilteredData({
		data: pendingRequest?.transferRequests ?? [],
		currentOrigin: InventoryTab.DEALER_TRADES,
		pathToVinNumber: "vehicle.vinNumber",
		commissionCycleId: commissionCycleId ?? "",
		payPeriodId: payPeriodId ?? "",
	});

	return (
		<>
			<Box mt={4}>
				<Typography component={"h2"} variant={"h2"}>
					Pending Request
				</Typography>
			</Box>
			{isLoadingPendingRequest || isFetching ? (
				<Box mt={4}>
					<TableLoader toolbar buttons={2} content rows={5} multiTitles={5} />
				</Box>
			) : (
				<PendingTransferRequestTable
					locationId={locationId}
					commissionCycleId={commissionCycleId}
					payPeriodId={payPeriodId}
					data={dataPendingRequests}
					hasEditPermission={hasEditPermission}
					actionRef={actionRef}
					isCurrentPayPeriod={isCurrentPayPeriod}
				/>
			)}
		</>
	);
}
